import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { BrandMark } from "../../components/Wordmark.js";

import { useSession } from "../../lib/session.js";
import { foregroundOn, initialOf } from "../../tokens/palette.js";

/**
 * One line: title, clock, the person legend, navigation, the view toggle and
 * the add button. The handoff is firm that this must not wrap — below 1280px
 * the page scrolls sideways instead, because a wrapped header changes the
 * whole composition.
 */
export function DesktopChrome({
  title,
  view,
  onPrev,
  onNext,
  onToday,
  onView,
}: {
  title: string;
  view: "week" | "month";
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onView: (view: "week" | "month") => void;
}): React.JSX.Element {
  const { people, zone, canEdit } = useSession();
  const navigate = useNavigate();
  const [clock, setClock] = useState(() => DateTime.now().setZone(zone));

  useEffect(() => {
    const tick = setInterval(() => setClock(DateTime.now().setZone(zone)), 30_000);
    return () => clearInterval(tick);
  }, [zone]);

  return (
    <header className="dk-head">
      {/*
        * Identity first, then what you are looking at — the order every desktop
        * calendar uses. The hand rather than the full lockup, because this line
        * is forbidden to wrap and the word will not fit beside six people; see
        * BrandMark. `title=""` since the month sits right beside it and "Frankie
        * August 2026" read aloud is one confusing date.
        */}
      <BrandMark height={26} className="dk-head__mark" title="" />
      <h1>{title}</h1>
      <span className="dk-clock">{clock.toFormat("h:mm a").toLowerCase()}</span>

      <div className="dk-legend">
        {people.map((person) => (
          <span key={person.id} className="dk-legend__item">
            <span
              className="disc"
              style={{
                width: 22,
                height: 22,
                background: person.color,
                color: foregroundOn(person.color),
                fontSize: 12,
              }}
            >
              {initialOf(person.display_name)}
            </span>
            {person.display_name}
          </span>
        ))}
      </div>

      <div className="dk-nav">
        <button type="button" onClick={onPrev} aria-label="Previous">
          ←
        </button>
        <button type="button" onClick={onToday}>
          Today
        </button>
        <button type="button" onClick={onNext} aria-label="Next">
          →
        </button>
      </div>

      <div className="dk-toggle">
        <button
          type="button"
          className={view === "week" ? "is-on" : ""}
          onClick={() => onView("week")}
        >
          Week
        </button>
        <button
          type="button"
          className={view === "month" ? "is-on" : ""}
          onClick={() => onView("month")}
        >
          Month
        </button>
      </div>

      {canEdit && (
        <button type="button" className="button-ink" onClick={() => void navigate("/event/new")}>
          Add event
        </button>
      )}
    </header>
  );
}
